/**
 * Error Boundary for Merchant Application Features
 * Provides graceful error handling and recovery for the merchant application section
 */

import React, { Component, ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: any;
  errorId: string | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  level?: 'component' | 'feature' | 'page';
  onError?: (error: Error, errorInfo: any) => void;
}

export class MerchantApplicationErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryCount = 0;
  private maxRetries = 3;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    const errorId = Math.random().toString(36).substr(2, 9);
    
    // Update state with error details
    this.setState({
      error,
      errorInfo,
      errorId,
    });

    // Log error to console
    console.error('Merchant Application Error Boundary:', {
      error,
      errorInfo,
      level: this.props.level || 'component',
      retryCount: this.retryCount,
      errorId,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error details for debugging
    console.group(`🚨 Merchant Application Error (ID: ${errorId})`);
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo.componentStack);
    console.groupEnd();
  }

  handleRetry = () => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      
      // Log retry attempt
      console.log(`🔄 Retrying merchant application (attempt ${this.retryCount}/${this.maxRetries})`);
      
      // Reset error state to retry rendering
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        errorId: null,
      });
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/app';
  };

  renderErrorFallback() {
    const { error, errorId } = this.state;
    const { level = 'component' } = this.props;
    
    const canRetry = this.retryCount < this.maxRetries;
    const isComponentLevel = level === 'component';

    if (isComponentLevel) {
      // Minimal error UI for component-level errors
      return (
        <Alert variant="destructive" className="my-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Component Error</AlertTitle>
          <AlertDescription className="mt-2">
            <p>This component encountered an error and couldn't render properly.</p>
            <div className="flex gap-2 mt-3">
              {canRetry && (
                <Button variant="outline" size="sm" onClick={this.handleRetry}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              )}
            </div>
          </AlertDescription>
        </Alert>
      );
    }

    // Full error UI for feature/page level errors
    return (
      <div className="min-h-[400px] flex items-center justify-center p-8">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle className="text-xl">Merchant Application Error</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <Bug className="h-4 w-4" />
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription>
                The merchant application feature encountered an unexpected error and couldn't load properly.
                {errorId && (
                  <div className="mt-2 text-sm font-mono bg-gray-100 p-2 rounded">
                    Error ID: {errorId}
                  </div>
                )}
              </AlertDescription>
            </Alert>

            {error && (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-800">
                  Technical Details
                </summary>
                <div className="mt-2 text-sm bg-gray-50 p-3 rounded border">
                  <div className="font-medium text-red-600 mb-2">Error:</div>
                  <div className="font-mono text-xs mb-3 break-all">{error.message}</div>
                  {error.stack && (
                    <>
                      <div className="font-medium text-red-600 mb-2">Stack Trace:</div>
                      <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap">
                        {error.stack}
                      </pre>
                    </>
                  )}
                </div>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              {canRetry && (
                <Button onClick={this.handleRetry} className="flex-1">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again ({this.maxRetries - this.retryCount} attempts left)
                </Button>
              )}
              
              <Button variant="outline" onClick={this.handleReload} className="flex-1">
                <RefreshCw className="h-4 w-4 mr-2" />
                Reload Page
              </Button>
              
              <Button variant="outline" onClick={this.handleGoHome} className="flex-1">
                <Home className="h-4 w-4 mr-2" />
                Go to Dashboard
              </Button>
            </div>

            <div className="text-center text-sm text-gray-500 mt-4">
              <p>If this problem persists, please contact support with the error ID above.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided, otherwise use built-in error UI
      return this.props.fallback || this.renderErrorFallback();
    }

    return this.props.children;
  }
}

// Higher-order component for wrapping components with error boundary
export function withMerchantApplicationErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <MerchantApplicationErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </MerchantApplicationErrorBoundary>
  );

  WrappedComponent.displayName = `withMerchantApplicationErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}

// Hook for functional components to report errors manually
export function useMerchantApplicationErrorHandler() {
  const reportError = React.useCallback((error: Error, context?: Record<string, any>) => {
    console.error('🚨 Merchant Application Error (Manual Report):', error, context);
  }, []);

  return { reportError };
}